import { useEffect, useState } from 'react';
import { minorToMajorString, parseMoneyToMinor } from '../lib/format';

// Money entry the way humans write it: MAJOR units with an explicit currency affordance
// ("250" or "1,000.50" — never "100050 minor units"). Emits integer minor units (or null
// while invalid/empty) so no float ever crosses the API boundary. Normalizes on blur.

export function MoneyInput({
  id,
  valueMinor,
  currency = 'USD',
  onChange,
  placeholder = '0.00',
  autoFocus,
  disabled,
  'aria-label': ariaLabel,
}: {
  id?: string;
  valueMinor: number | null;
  currency?: string;
  onChange: (minor: number | null) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
}) {
  const [text, setText] = useState(() => (valueMinor == null ? '' : minorToMajorString(valueMinor, currency)));
  const [focused, setFocused] = useState(false);

  // Follow external resets (e.g. a form clearing after submit) while not fighting typing.
  useEffect(() => {
    if (focused) return;
    setText(valueMinor == null ? '' : minorToMajorString(valueMinor, currency));
  }, [valueMinor, currency, focused]);

  const parsed = parseMoneyToMinor(text, currency);
  const invalid = text.trim() !== '' && parsed == null;

  return (
    <div className={`money-input ${invalid ? 'invalid' : ''}`}>
      <span className="mi-currency" aria-hidden>
        {currency}
      </span>
      <input
        id={id}
        inputMode="decimal"
        autoComplete="off"
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        value={text}
        autoFocus={autoFocus}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parseMoneyToMinor(e.target.value, currency));
        }}
        onBlur={() => {
          setFocused(false);
          if (parsed != null) setText(minorToMajorString(parsed, currency));
        }}
      />
    </div>
  );
}
